import { useEffect, useState } from 'react';

import { getSession, onAuthStateChange, type AuthSession, type SessionUser } from '@/lib/api';
import { logout } from '@/lib/api/auth';

/**
 * Capa de auth de commerce sobre el JWT propio (api-core), reemplazo de
 * `supabase.auth`.
 *
 * Se preservan las MISMAS firmas que ya consume la UI (`useSession` →
 * `{ session, user, loading }`, `signOut()`), de modo que los componentes no
 * cambian. El guard cross-user del carrito vive en `src/store/cart.ts`, que
 * escucha el mismo `onAuthStateChange` del tokenStore.
 */

export type Session = AuthSession;
export type User = SessionUser;

export function useSession(): {
  session: Session | null;
  user: User | null;
  loading: boolean;
} {
  const [session, setSessionState] = useState<Session | null>(() => getSession());
  // El tokenStore hidrata sincrónicamente desde localStorage, así que no hay
  // un estado de carga async real; mantenemos `loading` por compatibilidad de
  // firma y lo apagamos tras el primer effect.
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setSessionState(getSession());
    setLoading(false);

    const unsubscribe = onAuthStateChange((_event, next) => {
      setSessionState(next);
    });

    return unsubscribe;
  }, []);

  return { session, user: session?.user ?? null, loading };
}

export async function signOut(): Promise<void> {
  await logout();
}
